import {getters as AppModule} from "../../store/modules/app";
import {relatedTypeEnum} from "./sidebar";
import {$t} from "@/i18n";
import {getters as NoteModule} from "../../store/modules/note";
import {actions as ProjectModule} from "../../store/modules/project"
import {actions as DocumentsModule} from "../../store/modules/documents"


const reportLinks = {
    summary: "/report?view=summary" + "&dates_list=0000-00-00,0000-00-00,som_0,eom_0" + "&client_id_list=",
    task: "/report?view=detailed" + "&dates_list=0000-00-00,0000-00-00,som_0,eom_0" + "&client_id_list=",
    time: "/report?view=time_entries" + "&dates_list=0000-00-00,0000-00-00,som_0,eom_0" + "&client_id_list=",
}

const translations = [
    $t('Digitisation of client documents'),
    $t('Client portal')
]

export const getSidebarFieldComponent = (field) => {
    switch (field.type) {
        case "members":
        case "user_single":
        case "user_multi":
            return () => import("./Fields/MemberField.vue");
        case "client":
            return () => import("./Fields/MultipleClientField.vue");
        case "contacts":
            return () => import("./Fields/ClientContactsList.vue");
        case "emails":
            return () => import("../../views/Client/Overview/ClientEmailsList.vue");
        case "billing_notes":
        case "notes":
            return () => import("./Fields/NoteField.vue");
        case "reports":
            return () => import("./Fields/ReportsLinkField.vue");
        case "url":
        case "cloud_folder":
            return () => import("./Fields/LinkField.vue");
        case "cp_menu_links":
            return () => import("./Fields/CpMenuLinks.vue")
        default:
            return () => import("./Fields/BasicCustomField.vue");
    }
}

export const fieldTransformation = (field, relatedObject) => {
    // change field group name
    field.group_name = field.group_name ? field.group_name : getGroupName(field, relatedObject)

    // change field title
    const whitelistedFieldNames = ["parent_id"]
    if (whitelistedFieldNames.includes(field.name)) {
        const newTitle = getTitle(field, relatedObject)
        if (newTitle) {
            // Create a title for the backup as we will overwrite the existing one
            // Now we can return to the original title if the new title condition no longer matches
            field.backupTitle = field.title
            field.title = newTitle
        } else {
            field.title = field.backupTitle ? field.backupTitle : field.title
        }
    }
}

export const getGroupFieldValue = (field, relatedObject, relatedType) => {
    if (!field) return null
    switch (relatedType) {
        case relatedTypeEnum.CLIENT:
            return clientSpecificValue(field, relatedObject)
        case relatedTypeEnum.PROJECT:
            return projectTypeSpecificValue(field, relatedObject)
        default:
            return null
    }
}

const getTitle = (field, relatedObject) => {
    if (field.name === 'parent_id' && Array.isArray(relatedObject.subclients) && relatedObject.subclients.length > 0) {
        return $t("Sub-companies")
    }
    return null
}

const getGroupName = (field, relatedObject) => {
    if (!field) return ""
    if (field.group_name) return field.group_name

    // Edge cases / handle empty group names
    switch (true) {
        case field.related_type === 'contacts':
            return $t('Contacts');
        case field.related_type === relatedTypeEnum.CLIENT:
            return $t('Client company');
        case field.related_type.startsWith("project."):
            return getProjectDefaultGroupName(relatedObject);
        default:
            return ""
    }
}

const getProjectDefaultGroupName = (relatedObject) => {
    const accountId = relatedObject.account_id
    const isEventGroupName = accountId && ProjectModule.isEventTypeCompany(accountId)
    return isEventGroupName ? $t('Event info') : $t('Project info')
}

const defaultSpecificValue = (field, relatedObject, relatedType) => {
    switch (field.related_type) {
        case "notes": {
            const notes = NoteModule.getPinnedNotes(relatedObject.id, relatedType).map(note => note.id)
            return notes.length ? notes : [0]
        }
        default:
            return null
    }
}

const projectTypeSpecificValue = (field, project) => {
    switch (field.type) {
        case "project_status": {
            const choices = field._data?.choices ?? null
            if (choices) {
                return choices.find(c => c.code === project.status)?.name ?? null;
            }
            return null
        }
        default:
            if (field.type.startsWith('supplier.')) {
                const fieldName = `${field.name}_disp`
                return project[fieldName]
            }
            return defaultSpecificValue(field, project, relatedTypeEnum.PROJECT)
    }
}

const clientSpecificValue = (field, client) => {
    switch (field.related_type) {
        case "client":
            return clientTypeSpecificValue(field, client)
        case "tax_id": {
            const taxes = AppModule.getTaxes();
            const defaultTax = taxes[client.account_id].find((x) => x.id === client.tax_id);
            return defaultTax ? defaultTax.name ? defaultTax.name : defaultTax.description ? defaultTax.description : '' : '';
        }
        case "reports":
            return [
                {id: 1, link: reportLinks.summary + client.id, title: $t('Summary')},
                {id: 2, link: reportLinks.task + client.id, title: $t('Task')},
                {id: 3, link: reportLinks.time + client.id, title: $t('Time')}
            ]
        case "members":
            return client.members.map(member => member.id)
        case "billing_notes": {
            const notes = NoteModule.getPinnedNotes(client.id, relatedTypeEnum.CLIENT).map(note => note.id)
            return notes.length ? notes : [0]
        }
        default:
            return defaultSpecificValue(field, client, relatedTypeEnum.CLIENT)
    }
}

const clientTypeSpecificValue = (field, client) => {
    switch (field.name) {
        case "address_street":
            return [client.address_street, client.address_city, client.address_zip].filter(x => x).join(', ')
        case "parent_id": {
            if (Array.isArray(client.subclients) && client.subclients.length > 0) {
                return client.subclients;
            } else if (client.parent_client) {
                return [client.parent_client]
            }
            return [];

        }
        case "cloud_folder":
            if(client.fields?.cloud_folder?.service && client.fields?.cloud_folder?.enabled) {
                return DocumentsModule.getDriveLink(client.fields.cloud_folder.service, client.fields.cloud_folder.id)
            }
            return ''
        default:
            return null
    }
}
